import React, { memo } from "react";
import { TreatmentPlanAppointment } from "../../../../services/treatmentPlanService";
import { EditableAppointmentData } from "../../../../types/editableTreatmentPlan";
import { CDTAndCustomProcedureValue } from "../../../../components/CDTAndCustomProcedureSelect";
import TreatmentAppointment from "../TreatmentAppointment";
import EditTreatmentAppointment from "../EditTreatmentAppointment";
import { DraggableAppointmentWrapper } from "./TreatmentPlanAppointmentRow.styled";

export interface TreatmentPlanAppointmentRowProps {
  appointment: TreatmentPlanAppointment;
  index: number;
  isLastAppointment: boolean;
  draft: EditableAppointmentData;
  hasChanges: boolean;
  isEditing: boolean;
  isSaving: boolean;
  isDeleting: boolean;
  isDraggable: boolean;
  isDragging: boolean;
  isDragOver: boolean;
  onDragOver: (event: React.DragEvent) => void;
  onDragLeave: (event: React.DragEvent) => void;
  onDrop: (event: React.DragEvent) => void;
  onDraftAppointmentChange: (
    field: keyof EditableAppointmentData,
    value: unknown,
  ) => void;
  onDraftProcedureChange: (
    procedureIndex: number,
    field: keyof EditableAppointmentData["procedures"][number],
    value: unknown,
  ) => void;
  onDraftCdtChange: (
    procedureIndex: number,
    value: CDTAndCustomProcedureValue,
    allCdtCodes: string[],
    allDescriptions: string[],
    totalFee: number,
    totalDurationMinutes: number,
  ) => void;
  onSave: (durationMinutes: number) => void;
  onCancel: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  onDragStart: (event: React.DragEvent) => void;
  onDragEnd: () => void;
  showActionsColumn?: boolean;
}

const TreatmentPlanAppointmentRow: React.FC<TreatmentPlanAppointmentRowProps> = ({
  appointment,
  index,
  isLastAppointment,
  draft,
  hasChanges,
  isEditing,
  isSaving,
  isDeleting,
  isDraggable,
  isDragging,
  isDragOver,
  onDragOver,
  onDragLeave,
  onDrop,
  onDraftAppointmentChange,
  onDraftProcedureChange,
  onDraftCdtChange,
  onSave,
  onCancel,
  onEdit,
  onDelete,
  onDragStart,
  onDragEnd,
  showActionsColumn = true,
}) => (
  <DraggableAppointmentWrapper
    data-appointment-wrapper
    isDragging={isDragging}
    isDragOver={isDragOver}
    onDragOver={isEditing ? undefined : onDragOver}
    onDragLeave={isEditing ? undefined : onDragLeave}
    onDrop={isEditing ? undefined : onDrop}
    onDragEnd={isEditing ? undefined : onDragEnd}
  >
    {isEditing ? (
      <EditTreatmentAppointment
        appointmentId={appointment.id}
        appointmentNumber={index + 1}
        isLastAppointment={isLastAppointment}
        draft={draft}
        hasChanges={hasChanges}
        isSaving={isSaving}
        onDraftAppointmentChange={onDraftAppointmentChange}
        onDraftProcedureChange={onDraftProcedureChange}
        onDraftCdtChange={onDraftCdtChange}
        onSave={onSave}
        onCancel={onCancel}
      />
    ) : (
      <TreatmentAppointment
        appointment={appointment}
        appointmentNumber={index + 1}
        isLastAppointment={isLastAppointment}
        onEdit={onEdit}
        onDelete={onDelete}
        isDeleting={isDeleting}
        onDragStart={onDragStart}
        isDraggable={isDraggable}
        showActionsColumn={showActionsColumn}
      />
    )}
  </DraggableAppointmentWrapper>
);

export default memo(TreatmentPlanAppointmentRow);
